import posthog from 'posthog-js';

class Events {
	public readonly TEMP_ORDER_ID_PROPERTY_NAME = 'orderEventsUUID';
	public readonly APP_ID_PROPERTY_NAME = 'appId';
	public readonly APP_VERSION_PROPERTY_NAME = 'appVersion';

	private readonly EVENT_PAGE_VIEW = '$pageview';
	private readonly EVENT_ORDER_PLACED = 'order_placed';

	private key: string | null = null;
	private hasInitialized = false;

	isEnabled() {
		return !!this.key;
	}

	init(apiKey: string | null, config: Record<string, any> = {}) {
		this.key = apiKey;

		if(!this.key || this.hasInitialized) {
			return false;
		}

		try {
			posthog.init(
				this.key,
				{
					api_host: 'https://app.posthog.com',
					...config
				}
			);
		} catch(error) {
			console.warn('Failed to initialize PostHog', error);
		}

		// Even when we fail go ahead and flip this so we don't keep failing if called again
		this.hasInitialized = true;

		return true;
	}

	// Unlinks future events from user that was set
	reset() {
		if(!this.isEnabled()) {
			return false;
		}
		try {
			posthog.reset();
		} catch(error) {
			// Do nothing, PostHog seems to lose it's mind if you call this
			// in certain situations. I think this bug is what we are seeing
			// https://github.com/PostHog/posthog-js/issues/323
		}
	}

	// Function to identify the user for the session
	setUser(userBase: string | null) {
		if(!this.isEnabled()) {
			return false;
		}
		if(userBase === null) {
			return false;
		}
		try {
			posthog.identify(userBase);
		} catch(error) {
			console.warn('Failed to identify user base', error);
		}
	}

	// Function to register an order uuid to be associated with all subsequent events
	// Should be called when we first start an order
	registerOrderUUID(orderUUID: string) {
		if(!this.isEnabled()) {
			return false;
		}
		try {
			posthog.register({[this.TEMP_ORDER_ID_PROPERTY_NAME]: orderUUID});
		} catch(error) {
			console.warn('Failed to register order uuid', error);
		}
	}

	// Function to remove the orderUUID from all subsequent requests
	// Should be called upon order completion
	unregisterOrderUUID() {
		if(!this.isEnabled()) {
			return false;
		}
		try {
			posthog.unregister(this.TEMP_ORDER_ID_PROPERTY_NAME);
		} catch(error) {
			console.warn('Failed to unregister order uuid', error);
		}
	}

	registerAppInfo(appId: string, appVersion: string) {
		if(!this.isEnabled()) {
			return false;
		}

		try {
			posthog.register(
				{
					[this.APP_ID_PROPERTY_NAME]: appId,
					[this.APP_VERSION_PROPERTY_NAME]: appVersion
				}
			);
		} catch(error) {
			console.warn('Failed to register app info', error);
		}
	}

	unregisterAppInfo() {
		try {
			posthog.unregister(this.APP_ID_PROPERTY_NAME);
			posthog.unregister(this.APP_VERSION_PROPERTY_NAME);
		} catch(error) {
			console.warn('Failed to unregister app info', error);
		}
	}

	recordPageView(path: string) {
		if(!this.isEnabled()) {
			return false;
		}

		try {
			posthog.capture(this.EVENT_PAGE_VIEW, {
				$current_url: path
			});
		} catch(error) {
			console.warn('Failed to record page view', error);
		}
	}

	recordOrderPlaced(ticketId: string) {
		if(!this.isEnabled()) {
			return false;
		}

		try {
			posthog.capture(this.EVENT_ORDER_PLACED, {
				ticketId: ticketId
			});
		} catch(error) {
			console.warn('Failed to capture order placed event', error);
		}
	}
}

export const EventsService = new Events();
